// «Добавить почту» там, где человек встретился (005: сценарий 3, FR-012, FR-013, FR-015; принцип
// XV; SC-005): поле почты и «Сохранить почту» — явным сохранением, как адреса (ux.md, разделы 2
// и 13), без подстановки по шаблону. Enter сохраняет (форма из одного поля), Esc отменяет — слой
// закрывает тот, кто открыл форму. Формат проверяет сервер (DIR-001): что исправить — под полем,
// код и номер операции — в выноске над полем (T058 002). Пустое поле — ошибка под полем без
// запроса. Записано — тост: внешний адрес годен для писем после подтверждения, адрес уже у другого
// человека — предупреждение и пара «тот же?» в «К разбору». Пересчитываются адресат пунктов,
// готовность и «К разбору».
import { fieldMessages, fillNamed, isEmailAddress } from '@kp/contracts';
import type { RecordRef, SetEmailResponse } from '@kp/contracts';
import { Button, TextField, useToast } from '@kp/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { api, isApiError } from '../../api/client.ts';
import { FieldsRefusalCallout } from '../../app/FieldsRefusal.tsx';
import { errorView, useErrorToast } from '../../app/errors.ts';
import type { FieldsRefusal } from '../../app/errors.ts';
import { recordHref } from '../../shell/links.tsx';
import { useMe } from '../../shell/session.ts';
// Форма живёт и на «Главной» — стили вместе с ней.
import './control.css';

/** Что пересчитывается после новой почты: адресат пунктов, готовность, «К разбору», карточки. */
export const EMAIL_AFFECTED_ROUTES = [
  'control.items',
  'directory.triage',
  'directory.records.list',
  'directory.people.get',
  'directory.organizations.get',
  'directory.mentions.list',
  // 031 (FR-013, SC-002): «Готовы N из M» в протоколе пересчитывается после нового адреса.
  'readiness.get',
] as const;

/**
 * Адрес по формату — то же правило, что у сервера (isEmailAddress). Только чтобы убрать ошибку
 * под полем, когда значение стало верным (ux.md, раздел 13); проверяет формат сервер.
 */
export function looksLikeEmail(text: string): boolean {
  return isEmailAddress(text);
}

/** Домен примера — первый домен установки; без доменов — example.ru. */
function domainOf(domains: readonly string[]): string {
  return domains[0] ?? 'example.ru';
}

/** «Введите почту полностью, например morozov@example.ru». */
export function emptyEmailText(domains: readonly string[]): string {
  return fillNamed(fieldMessages.email, { домен: domainOf(domains) });
}

/** Внешний адрес записан, но для писем ещё не годен: ждёт подтверждения контролёра. */
function waitsConfirmation(response: SetEmailResponse): boolean {
  return (
    response.card.addressee.ready === false &&
    response.card.addressee.reason === 'email_unconfirmed'
  );
}

/**
 * Тост после записи: «Почта записана: Горбунов С. Б.»; внешний адрес — «… Адрес внешний: годен
 * для писем после подтверждения»; адрес уже у другого — «… Этот адрес уже у Киселёв З. А. —
 * пара «тот же?» в «К разбору»».
 */
export function emailSavedText(person: RecordRef, response: SetEmailResponse): string {
  const parts = [`Почта записана: ${person.name}`];
  if (waitsConfirmation(response)) parts.push('Адрес внешний: годен для писем после подтверждения');
  if (response.sameEmail) {
    parts.push(`Этот адрес уже у ${response.sameEmail.name} — пара «тот же?» в «К разбору»`);
  }
  // «Горбунов С. Б.» уже кончается точкой — вторую не ставим.
  return parts.reduce((text, part) => `${text}${text.endsWith('.') ? ' ' : '. '}${part}`);
}

export interface AddEmailFormProps {
  /** Чья почта: человек справочника. */
  person: RecordRef;
  /** Почта записана: форму закрывает тот, кто её открыл. */
  onSaved: (response: SetEmailResponse) => void;
  onCancel: () => void;
  /** Акцентная кнопка — в боковой панели; на «Главной» кнопок-акцентов в панелях нет. */
  accent?: boolean;
}

export function AddEmailForm({ person, onSaved, onCancel, accent = false }: AddEmailFormProps) {
  const me = useMe();
  const toast = useToast();
  const showError = useErrorToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [address, setAddress] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [refusal, setRefusal] = useState<FieldsRefusal<'address'> | null>(null);
  const [pending, setPending] = useState(false);
  const domain = domainOf(me.domains);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const submit = async (event?: FormEvent) => {
    event?.preventDefault();
    if (pending) return;
    setRefusal(null);
    if (address.trim() === '') {
      setError(emptyEmailText(me.domains));
      inputRef.current?.focus();
      return;
    }
    setPending(true);
    let response: SetEmailResponse;
    try {
      response = await api('directory.people.setEmail', {
        params: { id: person.id },
        body: { address: address.trim() },
      });
    } catch (failure) {
      setPending(false);
      if (isApiError(failure, 'DIR-001')) {
        // Что исправить — под полем; причина, код и номер операции — в выноске над ним.
        const text = failure.nextStep;
        setError(text);
        setRefusal({ fields: { address: text }, view: errorView(failure) });
        inputRef.current?.focus();
        return;
      }
      showError(failure, () => void submit());
      return;
    }
    setPending(false);
    for (const name of EMAIL_AFFECTED_ROUTES) {
      void queryClient.invalidateQueries({ queryKey: ['api', name] });
    }
    const notable = waitsConfirmation(response) || response.sameEmail !== null;
    toast.ok(emailSavedText(person, response), {
      duration: notable ? 'long' : 'normal',
      ...(notable
        ? {
            action: {
              label: 'Открыть карточку',
              onClick: () => void navigate({ href: recordHref(person) }),
            },
          }
        : {}),
    });
    onSaved(response);
  };

  return (
    <form className="t-col kp-email-form" noValidate onSubmit={(event) => void submit(event)}>
      <FieldsRefusalCallout refusal={refusal} errors={{ address: error }} />
      <TextField
        ref={inputRef}
        label={`Почта: ${person.name}`}
        type="email"
        inputMode="email"
        autoComplete="off"
        spellCheck={false}
        maxLength={254}
        required
        placeholder={`например, ivanov@${domain}`}
        hint={`Адрес на ${domain} годен для писем сразу, другой — после подтверждения`}
        error={error}
        value={address}
        onChange={(event) => {
          const next = event.target.value;
          setAddress(next);
          // Ошибка уходит, как только значение стало верным: пустое — заполнено, отказ по формату —
          // похоже на адрес. Окончательно формат проверит сервер при сохранении.
          if (error && (refusal ? looksLikeEmail(next) : next.trim() !== '')) setError(undefined);
        }}
      />
      <div className="t-row kp-email-actions">
        <Button size="sm" onClick={onCancel} disabled={pending}>
          Отмена
        </Button>
        <Button
          type="submit"
          size="sm"
          variant={accent ? 'primary' : 'secondary'}
          loading={pending}
        >
          Сохранить почту
        </Button>
      </div>
    </form>
  );
}
